import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ProjectCard.css';
import { useProject } from '../../../../contexts/ProjectContext';
import { getVirtualTourUrl, getVirtualTourCode, getProjectVirtualTours, getVirtualTourUrlByCode } from '../../../../utils/virtualTourMapping';
import unrealProjectsService from '../../../../services/unrealProjectsService';
import ec2InstanceService from '../../../../services/ec2InstanceService';
import { useEC2Loading } from '../../../../contexts/EC2LoadingContext';
import authService from '../../../../services/authService';
import EC2LoadingBar from './EC2LoadingBar';
import ManageProjectUsersModal from './ManageProjectUsersModal';

const ProjectCard = ({ project, onProjectSelect, onViewProject, formatDate, user, customRedirectPath }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showEC2Loading, setShowEC2Loading] = useState(false);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const menuRef = useRef(null);
  const { startLoading, stopLoading, isProjectLoading } = useEC2Loading();
  
  // Check if this project is loading from global context
  const isGloballyLoading = isProjectLoading(project.Id);
  
  // Use project context utilities
  const { isProjectExpired, canViewProject, canAccessProjectFiles, formatFileSize } = useProject();

  const isExpired = isProjectExpired(project);
  const canView = canViewProject(project);
  const canAccessFiles = canAccessProjectFiles(project);

  const handleImageError = () => {
    setImageError(true);
  };

  const getProjectInitial = () => {
    return project.Name ? project.Name.charAt(0).toUpperCase() : 'P';
  };

  const getGradientClass = () => {
    const firstLetter = getProjectInitial();
    return `gradient-${firstLetter}`;
  };

  const getProjectThumbnail = () => {
    if (imageError) {
      console.log('🖼️ ProjectCard - Image error, returning null for fallback');
      return null; // Will show fallback
    }
    
    // Debug ThumbnailURL
    console.log('🖼️ ProjectCard - ThumbnailURL debug:');
    console.log('  🖼️ ThumbnailURL value:', project.ThumbnailURL);
    console.log('  🖼️ ThumbnailURL type:', typeof project.ThumbnailURL);
    console.log('  🖼️ ThumbnailURL === null:', project.ThumbnailURL === null);
    console.log('  🖼️ ThumbnailURL === undefined:', project.ThumbnailURL === undefined);
    console.log('  🖼️ ThumbnailURL === "null":', project.ThumbnailURL === 'null');
    
    // Check if ThumbnailURL is null, undefined, or empty string
    if (!project.ThumbnailURL || project.ThumbnailURL === 'null' || project.ThumbnailURL.trim() === '') {
      console.log('🖼️ ProjectCard - ThumbnailURL is null/empty, returning null for fallback');
      return null; // Will show fallback
    }
    
    const thumbnailUrl = `${project.ThumbnailURL}?${Date.now()}`;
    console.log('🖼️ ProjectCard - Generated thumbnail URL:', thumbnailUrl);
    return thumbnailUrl;
  };


  const handleMenuToggle = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      // Check if there's enough space below for the menu
      if (menuRef.current) {
        const buttonRect = menuRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const estimatedMenuHeight = 300; // Approximate height of the menu
        
        // If not enough space below, open upward
        setMenuOpensUpward(spaceBelow < estimatedMenuHeight);
      }
    }
    
    setShowMenu(!showMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    switch (action) {
      case 'settings':
        // TODO: Navigate to project settings
        console.log('Project settings:', project.Id);
        break;
      case 'share':
        // TODO: Navigate to project sharing
        console.log('Share project:', project.Id);
        break;
      case 'rename':
        // TODO: Show rename dialog
        console.log('Rename project:', project.Id);
        break;
      case 'download':
        // TODO: Download project shortcut
        window.location.href = `/api/shortcut/project?projectId=${project.Id}`;
        break;
      case 'downloadVTour':
        // TODO: Download virtual tour shortcut
        window.location.href = `/api/shortcut/vtour?projectId=${project.Id}`;
        break;
      case 'leave':
        // TODO: Show leave project dialog
        console.log('Leave project:', project.Id);
        break;
      case 'copy':
        // TODO: Copy project model
        console.log('Copy project model:', project.Id);
        break;
      case 'paste':
        // TODO: Paste project model
        console.log('Paste project model:', project.Id);
        break;
      case 'manageUsers':
        setShowManageUsersModal(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`project-card gallery-view ${isExpired ? 'expired' : ''}`}>
      {/* EC2 Loading Bar - Show if globally loading */}
      {isGloballyLoading && (
        <EC2LoadingBar
          projectId={project.Id}
          onComplete={() => {
            stopLoading(project.Id);
            // Get the external URL and open it
            unrealProjectsService.getProjectExternalUrl(project.Id).then(externalUrl => {
              if (externalUrl) {
                console.log(`🚀 Opening external URL after EC2 startup:`, externalUrl);
                window.open(externalUrl, '_blank', 'noopener,noreferrer');
              }
            }).catch(error => {
              console.error('Error getting external URL after EC2 startup:', error);
            });
          }}
          onError={(error) => {
            console.error('EC2 loading bar error:', error);
            stopLoading(project.Id);
            // Show error message or fallback
            alert(`Failed to start EC2 instance: ${error.message}`);
          }}
          onStopping={() => {
            console.log('EC2 instance is stopping, hiding loading bar');
            stopLoading(project.Id);
            // No alert shown for stopping state
          }}
        />
      )}
      
      <div className={`project-thumbnail ${getGradientClass()}`}>
        {getProjectThumbnail() && !imageError ? (
          <img 
            src={getProjectThumbnail()} 
            alt={project.Name}
            onError={handleImageError}
          />
        ) : (
          <div className="project-initial-large">{getProjectInitial()}</div>
        )}
        <div 
          className="project-3d-overlay"
          onClick={async (e) => {
            console.log(`🎯 3D Overlay clicked for project ${project.Id}!`);
            e.stopPropagation();
            if (!isExpired && canView) {
              // Check if this project has a custom redirect path
              if (customRedirectPath) {
                console.log(`🎯 Using custom redirect path for project ${project.Id}:`, customRedirectPath);
                window.location.href = customRedirectPath;
                return;
              }
              
              // Check if this project has an external Unreal Engine URL
              console.log(`🔍 Checking for external URL for project ${project.Id}...`);
              
              // Force refresh to get latest data from AWS Secrets Manager
              await unrealProjectsService.forceRefresh();
              const externalUrl = await unrealProjectsService.getProjectExternalUrl(project.Id);
              console.log(`🔍 External URL result for project ${project.Id}:`, externalUrl);
              
              if (externalUrl) {
                // Show loading bar IMMEDIATELY for instant feedback
                console.log(`🚀 Showing loading bar immediately for project ${project.Id}`);
                startLoading(project.Id, project.Name);
                
                // Check if this project has an EC2 instance configured
                console.log(`🔧 Attempting to check EC2 instance for project ${project.Id}...`);
                try {
                  const instanceInfo = await ec2InstanceService.getInstanceInfo(project.Id);
                  console.log(`🔧 EC2 instance info for project ${project.Id}:`, instanceInfo);
                  
                  if (!instanceInfo.instanceName) {
                    // No EC2 instance configured, hide loading and open URL directly
                    console.log(`🚀 No EC2 instance configured, opening external URL directly for project ${project.Id}:`, externalUrl);
                    stopLoading(project.Id);
                    
                    // Attach authentication token for 123bimlive.com URLs
                    const authenticatedUrl = await authService.getAuthenticatedUrl(externalUrl);
                    console.log(`🔐 Opening authenticated URL:`, authenticatedUrl);
                    window.open(authenticatedUrl, '_blank', 'noopener,noreferrer');
                  }
                  // If instance exists, the loading bar will continue and handle the startup
                } catch (error) {
                  console.error(`❌ Error checking EC2 instance for project ${project.Id}:`, error);
                  console.error(`❌ Error details:`, error.message);
                  console.error(`❌ Full error:`, error);
                  // Hide loading bar and fallback to opening URL directly
                  console.log(`🚀 Fallback: Opening external URL directly due to error`);
                  stopLoading(project.Id);
                  
                  // Attach authentication token for 123bimlive.com URLs
                  const authenticatedUrl = await authService.getAuthenticatedUrl(externalUrl);
                  console.log(`🔐 Opening authenticated URL:`, authenticatedUrl);
                  window.open(authenticatedUrl, '_blank', 'noopener,noreferrer');
                }
              } else {
                // Use internal 3D view
                console.log(`🏠 No external URL found, opening internal 3D view for project ${project.Id}`);
                onViewProject(project);
              }
            }
          }}
          style={{ 
            cursor: (!isExpired && canView) ? 'pointer' : 'default',
            pointerEvents: (!isExpired && canView) ? 'auto' : 'none'
          }}
        >
          <span className="project-3d-icon">explore</span>
          <span className="project-3d-text">3D View</span>
        </div>
        
        {isExpired && (
          <div className="expired-overlay">
            <i className="fas fa-lock"></i>
            <h4>Project Expired</h4>
            <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
            <button 
              className="leave-btn"
              onClick={(e) => handleMenuAction('leave', e)}
            >
              Leave Project
            </button>
          </div>
        )}
        
        <div className={`project-card-menu ${showMenu ? 'show' : ''}`} ref={menuRef}>
          <button 
            className="project-menu-trigger" 
            onClick={handleMenuToggle}
            disabled={isExpired}
          >
            <div className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
          
          {showMenu && (
            <div className={`project-menu-dropdown ${menuOpensUpward ? 'open-upward' : ''}`}>
              {isExpired ? (
                <div className="expired-menu-content">
                  <h4>Project Expired</h4>
                  <p>Contact <a href="mailto:sales@rdvsystems.com">sales@rdvsystems.com</a></p>
                  <button 
                    className="leave-btn"
                    onClick={(e) => handleMenuAction('leave', e)}
                  >
                    Leave Project
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={(e) => handleMenuAction('view', e)}>
                    <i className="fas fa-eye"></i>
                    View Project
                  </button>
                  <button onClick={(e) => handleMenuAction('files', e)}>
                    <i className="fas fa-folder-open"></i>
                    Project Files
                  </button>
                  <button onClick={(e) => handleMenuAction('manageUsers', e)}>
                    <i className="fas fa-users-cog"></i>
                    Manage Project Users
                  </button>
                  <button onClick={(e) => handleMenuAction('vtour', e)}>
                    <i className="fas fa-vr-cardboard"></i>
                    Virtual Tour
                  </button>
                  <button onClick={(e) => handleMenuAction('leave', e)}>
                    <i className="fas fa-sign-out-alt"></i>
                    Leave Project
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="project-info">
        <h3 className="project-title">{project.Name}</h3>
        
        <div className="project-meta">
          <div className="project-meta-left">
            <span>Last Modified: {formatDate(project.Modified)}</span>
            {project.UserRoleId < 2 && (
              <span className="expiry">Exp. Date: {project.Expiration ? (project.Expiration instanceof Date ? project.Expiration.toLocaleDateString() : new Date(project.Expiration).toLocaleDateString()) : 'N/A'}</span>
            )}
          </div>
          
          <div className="project-meta-bottom">
            <span className="role">{project.UserRole}</span>
            
            <div className="project-actions">
              {canAccessFiles && (
                <button 
                  className="action-link files"
                  onClick={() => onProjectSelect(project)}
                  disabled={isExpired}
                >
                  <i className="fas fa-folder-open"></i>
                  Project Files
                </button>
              )}
              
              {/* Always render VirtualTourButton - it will decide internally if it should show */}
              <VirtualTourButton 
                project={project} 
                isExpired={isExpired}
              />
            </div>
          </div>
        </div>
      </div>
      
      
      <ManageProjectUsersModal
        projectId={project.Id}
        projectName={project.Name}
        isOpen={showManageUsersModal}
        onClose={() => setShowManageUsersModal(false)}
      />
    </div>
  );
};

// Virtual Tour Dropdown Component
const VirtualTourButton = ({ project, isExpired }) => {
  const [virtualTours, setVirtualTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState({});

  // Load virtual tours on component mount
  useEffect(() => {
    const loadVirtualTours = async () => {
      setIsLoading(true);
      try {
        console.log(`🔍 VirtualTourButton: Loading tours for project ${project.Id}...`);
        const tours = await getProjectVirtualTours(project.Id);
        console.log(`🔍 VirtualTourButton: Received ${tours?.length || 0} tours for project ${project.Id}:`, tours);
        setVirtualTours(tours || []);
      } catch (error) {
        console.error(`❌ VirtualTourButton: Failed to load tours for project ${project.Id}:`, error);
        setVirtualTours([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadVirtualTours();
  }, [project.Id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideToggle = dropdownRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideToggle && !clickedInsideMenu) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleVirtualTourClick = (e, tour) => {
    e.stopPropagation();
    const virtualTourUrl = getVirtualTourUrlByCode(tour.code);
    console.log('🔗 Opening virtual tour URL:', virtualTourUrl, 'for tour:', tour.vtour_name);
    window.open(virtualTourUrl, '_blank', 'noopener,noreferrer');
    setShowDropdown(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isExpired && virtualTours.length > 0) {
      // Check available space and determine if dropdown should open upwards
      if (!showDropdown && toggleRef.current) {
        const buttonRect = toggleRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Estimate dropdown height (each item ~56px + padding)
        const estimatedDropdownHeight = Math.min(virtualTours.length * 56 + 32, 400);
        
        const shouldOpenUpwards = spaceBelow < estimatedDropdownHeight && spaceAbove > estimatedDropdownHeight;

        const topPosition = shouldOpenUpwards
          ? buttonRect.top + window.scrollY - estimatedDropdownHeight - 8
          : buttonRect.bottom + window.scrollY + 8;

        setDropdownStyles({
          position: 'absolute',
          top: topPosition,
          left: buttonRect.left + window.scrollX,
          minWidth: Math.max(buttonRect.width, 280),
          maxHeight: estimatedDropdownHeight
        });
      }
      setShowDropdown(!showDropdown);
    } else if (!isExpired && virtualTours.length === 1) {
      // If only one tour, open it directly
      handleVirtualTourClick(e, virtualTours[0]);
    }
  };

  // Don't render if no tours available (and not loading)
  if (!isLoading && virtualTours.length === 0) {
    console.log(`🔍 VirtualTourButton: No tours for project ${project.Id}, hiding button`);
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <button 
        className="action-link vtour"
        disabled={true}
        title="Loading virtual tours..."
      >
        <i className="fas fa-spinner fa-spin"></i>
        Loading...
      </button>
    );
  }

  // If only one tour, render as a simple button
  if (virtualTours.length === 1) {
    console.log(`🔍 VirtualTourButton: Showing single tour for project ${project.Id}`);

    return (
      <button 
        className="action-link vtour"
        onClick={(e) => handleVirtualTourClick(e, virtualTours[0])}
        disabled={isExpired || isLoading}
        title={virtualTours[0].vtour_name}
      >
        <i className="fas fa-vr-cardboard"></i>
        {isLoading ? 'Loading...' : "Virtual Tour"}
      </button>
    );
  }

  // Render dropdown for multiple tours
  console.log(`🔍 VirtualTourButton: Showing dropdown with ${virtualTours.length} tours for project ${project.Id}`);

  const renderDropdownMenu = () => (
    <div
      className="vtour-dropdown-menu"
      style={dropdownStyles}
      ref={menuRef}
    >
      {virtualTours.map((tour, index) => (
        <div 
          key={index}
          className="vtour-dropdown-item"
          onClick={(e) => handleVirtualTourClick(e, tour)}
        >
          <i className="fas fa-vr-cardboard"></i>
          <span className="vtour-name">{tour.vtour_name}</span>
          <span className="vtour-code">({tour.code})</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="vtour-dropdown-container" ref={dropdownRef}>
      <button 
        className={`action-link vtour vtour-dropdown-toggle ${showDropdown ? 'active' : ''}`}
        onClick={toggleDropdown}
        disabled={isExpired}
        title="Virtual Tours"
        ref={toggleRef}
      >
        <i className="fas fa-vr-cardboard"></i>
        {`Virtual Tours`}
        <i className={`fas fa-chevron-down dropdown-arrow ${showDropdown ? 'rotate' : ''}`}></i>
      </button>
      
      {showDropdown && !isExpired && (
        typeof document === 'undefined'
          ? renderDropdownMenu()
          : createPortal(renderDropdownMenu(), document.body)
      )}
    </div>
  );
};

export default ProjectCard;
